import { makeStyles, tokens } from '@fluentui/react-components';
import React, { useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { userContextSlice } from '@tdp/common/auth';
import { LoadingStates } from '@tdp/common/constants';
import {
  notificationSlice,
  NotificationType,
  TopBannerContentType,
} from '@tdp/common/notification';
import { ScreenSizes, useScreenSize } from '@tdp/common/shared';
import { DeveloperPortalRootContext, useCommand, useDispatch } from '@tdp/core';
import { appListSlice } from '@tdp/modules/apps';

import { AppListConfirmDialog } from './AppListConfirmDialog';
import AppListDataGrid from './AppListDataGrid';
import { AppListDuplicateDialog } from './AppListDuplicateDialog';
import { AppListEmpty } from './AppListEmpty';
import { AppListFirstPartyImportCatalog } from './AppListFirstPartyImportCatalog';
import { AppListFirstPartyPublishStatus } from './AppListFirstPartyPublishStatus';
import { AppListImportDialog } from './AppListImportDialog';
import { AppListNewDialog } from './AppListNewDialog';
import { AppListPageHeader } from './AppListPageHeader';
import { AppListTableSkeleton } from './AppListSkeleton';
import AppListTableHeader from './AppListTableHeader';
import { AppListTakeOwnershipDialog } from './AppListTakeOwnershipDialog';
import { IAppSummary } from '../abstractions/IAppSummary';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    paddingRight: tokens.spacingHorizontalXL,
    paddingLeft: tokens.spacingHorizontalXL,
  },
  tableContainer: {
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow2,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '1600px',
  },
});

const AppListTable: React.FC = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const loadApps = useCommand((commands) => commands.appListCommands.loadApps);
  const getAppsCount = useCommand(
    (commands) => commands.appListCommands.getAppsCount
  );
  const deleteApp = useCommand(
    (commands) => commands.appListCommands.deleteApp
  );
  const download = useCommand(
    (commands) => commands.appListCommands.downloadApp
  );
  const loadingStatus = useSelector(appListSlice.selectors.loading);
  const apps = useSelector(appListSlice.selectors.apps);
  const appsCount = useSelector(appListSlice.selectors.appsCount);
  const currentPage = useSelector(appListSlice.selectors.currentPage);
  const isTenantAdmin = !!useSelector(userContextSlice.selectors.isTenantAdmin);
  const isMicrosoft = !!useSelector(
    userContextSlice.selectors.isMicrosoftTenant
  );

  // Local states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [newDialogOpen, setNewDialogOpen] = React.useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [takeOwnershipDialogOpen, setTakeOwnershipDialogOpen] =
    React.useState(false);
  const [firstPartyPublishingStatus, setFirstPartyPublishingStatus] =
    React.useState(false);
  const [firstPartyImportCatalog, setFirstPartyImportCatalog] =
    React.useState(false);
  const [activeApp, setActiveApp] = React.useState<Partial<IAppSummary> | null>(
    null
  );
  const [appsItems, setAppsItems] = React.useState<IAppSummary[]>([]);
  const [filterString, setFilterString] = React.useState<string>('');
  const [liveMessage, setLiveMessage] = React.useState('');
  const { services: { logger } } = useContext(DeveloperPortalRootContext);

  useEffect(() => {
    logger.logUserBi({ telemetryMarker: 'apps-view-apps-list-page-landed', message: "User landed on App list page" });
    dispatch(
      notificationSlice.actions.showTopBanner({
        type: TopBannerContentType.AppListEnvironment,
        show: true,
      })
    );
    return () => {
      dispatch(notificationSlice.actions.dismissTopBanner());
    };
  }, [logger]);

  React.useEffect(() => {
    if (loadingStatus === LoadingStates.SUCCESS) {
      logger.logUserBi({
        telemetryMarker: 'apps-view-apps-list-page-rendered',
        message: `App list rendered on screen. Count: ${appsItems.length}`,
      });
    }
  }, [loadingStatus, appsItems.length, logger]);

  useEffect(() => {
    if (loadingStatus === LoadingStates.NOT_STARTED) {
      getAppsCount();
    }
  }, [apps]);

  useEffect(() => {
    if (appsCount > 0) {
      const getPage = Math.floor(apps.ids.length / 15) + 1;
      if (getPage >= currentPage) {
        loadApps(getPage);
      }
    }
  }, [apps, appsCount, loadingStatus]);

  useEffect(() => {
    if (apps) {
      const appsList: IAppSummary[] = [];
      apps.ids.forEach((id) => {
        const app = apps.entities[id];
        if (
          app &&
          app.appName?.toLowerCase().includes(filterString.toLowerCase()) &&
          (!app.environments || app.environments.length < 1)
        ) {
          appsList.push(app);
        }
      });
      setAppsItems(appsList);
    }
  }, [apps, filterString]);

  React.useEffect(() => {
    if (filterString) {
      setLiveMessage(
        intl.formatMessage(
          {
            defaultMessage: `Found {count} apps for filter "{filter}".`,
            description:
              'Message indicating the number of apps found for a specific filter',
          },
          {
            count: appsItems.length,
            filter: filterString,
          }
        )
      );
    } else {
      setLiveMessage(
        intl.formatMessage(
          {
            defaultMessage: `Total {count} apps are listed.`,
            description:
              'Message indicating the number of apps listed initially',
          },
          {
            count: appsItems.length,
          }
        )
      );
    }
  }, [appsItems, filterString]);

  const showDeleteDialog = (appId: string) => {
    setDeleteDialogOpen(true);
    setActiveApp({
      appId: appId,
    });
  };

  const showNewDialog = () => {
    setNewDialogOpen(true);
  };

  const onDuplicate = (appId: string) => {
    setActiveApp({
      appId: appId,
    });
    setDuplicateDialogOpen(true);
  };

  const onDeleted = () => {
    if (activeApp?.appId) {
      deleteApp(activeApp.appId);
    }
  };

  const onDownload = (appId: string) => {
    download(appId);
  };

  return (
    <div className={classes.container}>
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {liveMessage}
      </div>
      {newDialogOpen && (
        <AppListNewDialog open={newDialogOpen} setOpen={setNewDialogOpen} />
      )}
      {duplicateDialogOpen && (
        <AppListDuplicateDialog
          sourceApp={activeApp}
          isOpen={duplicateDialogOpen}
          setOpen={setDuplicateDialogOpen}
        />
      )}
      {importDialogOpen && (
        <AppListImportDialog
          open={importDialogOpen}
          setOpen={setImportDialogOpen}
        />
      )}
      {takeOwnershipDialogOpen && (
        <AppListTakeOwnershipDialog
          open={takeOwnershipDialogOpen}
          setOpen={setTakeOwnershipDialogOpen}
        />
      )}
      {firstPartyPublishingStatus && (
        <AppListFirstPartyPublishStatus
          open={firstPartyPublishingStatus}
          setOpen={setFirstPartyPublishingStatus}
        />
      )}
      {firstPartyImportCatalog && (
        <AppListFirstPartyImportCatalog
          open={firstPartyImportCatalog}
          setOpen={setFirstPartyImportCatalog}
        />
      )}
      <AppListConfirmDialog
        action={() => {
          onDeleted();
        }}
        message={intl.formatMessage({
          defaultMessage: `Are you sure you want to delete this app ?`,
        })}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onCancel={() => {
          setActiveApp(null);
        }}
        title={intl.formatMessage({ defaultMessage: `Delete App ?` })}
      />
      <AppListPageHeader />
      <div className={classes.tableContainer}>
        <AppListTableHeader
          length={appsItems.length}
          isMicrosoft={isMicrosoft}
          isTenantAdmin={isTenantAdmin}
          newDialogOpen={showNewDialog}
          importDialogOpen={setImportDialogOpen}
          ownerDialogOpen={setTakeOwnershipDialogOpen}
          firstPartyPublishingStatus={setFirstPartyPublishingStatus}
          setFirstPartyImportCatalog={setFirstPartyImportCatalog}
          onAppsFilter={(str: string) => {
            setFilterString(str);
          }}
        />
        {loadingStatus === LoadingStates.SUCCESS ? (
          appsItems.length === 0 ? (
            <AppListEmpty setNewDialogOpen={setNewDialogOpen} />
          ) : (
            <AppListDataGrid
              showDeleteDialog={showDeleteDialog}
              apps={appsItems}
              onDownload={onDownload}
              onDuplicate={onDuplicate}
            />
          )
        ) : (
          <AppListTableSkeleton />
        )}
      </div>
    </div>
  );
};

export default AppListTable;
